// Live Bitcoin pricing for the per-leaf spend-cost readout.
//
// One client-side fetch of mempool.space's public, CORS-open, key-less API: the
// recommended fee rate (sat/vB) and the BTC price in several fiats. Cached at
// module scope for the session; falls back to fixed defaults if offline. The IDE
// stays serverless: this is just a browser fetch, no backend.

import { useEffect, useState } from "react";

export const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "CHF", "AUD", "JPY"] as const;
export type Currency = (typeof CURRENCIES)[number];

export interface Pricing {
  feeRate: number; // sat/vB (mempool "halfHourFee")
  prices: Record<string, number>; // BTC price per fiat code
  live: boolean; // true once the live fetch succeeded
}

// Rough fixed values used only when offline; clearly marked live:false.
const FALLBACK: Pricing = {
  feeRate: 6,
  prices: { USD: 100000, EUR: 92000, GBP: 78000, CAD: 138000, CHF: 88000, AUD: 152000, JPY: 15800000 },
  live: false,
};

let cache: Pricing | null = null;
let inflight: Promise<Pricing> | null = null;

async function fetchPricing(): Promise<Pricing> {
  try {
    const [feesR, pricesR] = await Promise.all([
      fetch("https://mempool.space/api/v1/fees/recommended"),
      fetch("https://mempool.space/api/v1/prices"),
    ]);
    const fees = (await feesR.json()) as Record<string, number>;
    const prices = (await pricesR.json()) as Record<string, number>;
    const feeRate = Number(fees.halfHourFee) || FALLBACK.feeRate;
    const out: Record<string, number> = {};
    for (const c of CURRENCIES) if (typeof prices[c] === "number" && prices[c] > 0) out[c] = prices[c];
    return { feeRate, prices: Object.keys(out).length ? out : FALLBACK.prices, live: true };
  } catch {
    return FALLBACK;
  }
}

/** Fetch-once-and-cache pricing; returns the fallback until the live data lands. */
export function usePricing(): Pricing {
  const [p, setP] = useState<Pricing>(cache ?? FALLBACK);
  useEffect(() => {
    if (cache) {
      setP(cache);
      return;
    }
    if (!inflight) inflight = fetchPricing().then((r) => (cache = r));
    let alive = true;
    void inflight.then((r) => {
      if (alive) setP(r);
    });
    return () => {
      alive = false;
    };
  }, []);
  return p;
}

/** Spend cost in sats: virtual bytes (weight / 4) times the fee rate. Pass the
 *  per-input weight for the honest cost, or the witness weight to compare paths. */
export function spendSats(weightUnits: number, feeRate: number): number {
  return Math.round((weightUnits / 4) * feeRate);
}

export function fmtSats(sats: number): string {
  return sats.toLocaleString("en-US");
}

/** Format a sats amount as the chosen fiat (small amounts keep extra precision). */
export function fmtFiat(sats: number, price: number, ccy: string): string {
  const v = (sats * price) / 1e8;
  const opts: Intl.NumberFormatOptions =
    ccy === "JPY"
      ? { style: "currency", currency: "JPY", maximumFractionDigits: 0 }
      : { style: "currency", currency: ccy, minimumFractionDigits: 2, maximumFractionDigits: v < 0.01 ? 4 : 2 };
  try {
    return new Intl.NumberFormat(undefined, opts).format(v);
  } catch {
    return `${v.toFixed(2)} ${ccy}`;
  }
}
